"use client";

import React, { Suspense } from 'react'
import Link from "next/link";
import Image from "next/image";
import { CategoryData, EventResponse, Merchant, Promotion } from '@/services/dataTypes'
import NavSearch from './NavSearch';
import { getBaseImageUrl, getEventsHref, getMerchantHref, getPromotionHref } from '@/constants/hooks';
import MobileNavMenu from './MobileNavMenu';
import { faChevronDown, faArrowRight, faFire, faCalendarCheck, faTags, faBolt, faStore } from '@/constants/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface Props {
    merchantData: Merchant[],
    categories: CategoryData[] | any,
    headerPromoMerchant: Merchant[] | null,
    companyDomain: string,
    unique_id: string,
    mer_slug: string,
    mer_slug_type: string,
    promo_slug: string,
    cat_slug: string,
    blog_url?: string,
    blog_title?: string,
    blog_slug?: string,
    blog_source?: string,
    company_logo: string | null,
    events: EventResponse[],
    promotions: Promotion[],
}

const NavbarSec = ({
    unique_id, merchantData, categories, headerPromoMerchant,
    companyDomain, mer_slug, mer_slug_type, cat_slug,
    blog_url, blog_title, blog_slug, blog_source, company_logo, events, promotions, promo_slug
}: Props) => {
    const companyLogo = getBaseImageUrl(companyDomain, company_logo, "/themes/Theme_3/images/logo.png");
    const currentBlogUrl = blog_source === 'internal' && blog_slug
        ? `/${blog_slug.replace(/^\/+|\/+$/g, '')}`
        : blog_url;

    /* ────────────────────────────────────────────
       Category card — image-led, hover lift
    ──────────────────────────────────────────── */
    const CategoryCard = ({ href, name, iconSrc }: { href: string; name: string; iconSrc?: string }) => (
        <Link
            href={href}
            className="group/cat relative flex flex-col items-center gap-2 p-3 rounded-2xl
                       border border-transparent hover:border-[#D61C4E]/15
                       hover:bg-gradient-to-b hover:from-[#D61C4E]/5 hover:to-transparent
                       transition-all duration-300 hover:-translate-y-0.5 text-center"
        >
            {/* Icon box */}
            <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-100
                            group-hover/cat:border-[#D61C4E]/20 group-hover/cat:bg-white
                            group-hover/cat:shadow-md group-hover/cat:shadow-[#D61C4E]/10
                            flex items-center justify-center transition-all duration-300 overflow-hidden shrink-0">
                {iconSrc
                    ? <Image src={iconSrc} alt="" height={30} width={30} className="object-contain" unoptimized />
                    : <span className="text-lg">🏷️</span>
                }
            </div>
            <span className="text-[10px] font-bold text-slate-600 group-hover/cat:text-[#D61C4E]
                             tracking-wider transition-colors w-full">
                {name}
            </span>
        </Link>
    );

    /* ────────────────────────────────────────────
       Store / merchant row item
    ──────────────────────────────────────────── */
    const StoreItem = ({ href, name, iconSrc }: { href: string; name: string; iconSrc?: string }) => (
        <Link
            href={href}
            className="group/st flex items-center gap-3 px-3 py-2.5 rounded-xl
                       hover:bg-[#D61C4E]/5 border border-transparent
                       hover:border-[#D61C4E]/10 transition-all duration-200"
        >
            <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-white border border-slate-100
                            group-hover/st:border-[#D61C4E]/20 group-hover/st:shadow-sm
                            flex items-center justify-center transition-all overflow-hidden">
                {iconSrc
                    ? <Image src={iconSrc} alt="" height={28} width={28} className="object-contain" unoptimized />
                    : <FontAwesomeIcon icon={faStore} className="text-slate-300 text-sm" />
                }
            </div>
            <div className="flex flex-col min-w-0 flex-1">
                <span className="text-[12px] font-semibold text-slate-700
                                group-hover/st:text-[#D61C4E] truncate transition-colors">
                    {name}
                </span>
                <span className="text-[9px] text-slate-400 uppercase tracking-wide">Verified</span>
            </div>
            <FontAwesomeIcon icon={faArrowRight}
                className="text-[8px] text-slate-300 group-hover/st:text-[#D61C4E]
                           group-hover/st:translate-x-0.5 transition-all flex-shrink-0" />
        </Link>
    );

    /* ────────────────────────────────────────────
       Simple list item (promotions / events)
    ──────────────────────────────────────────── */
    const ListItem = ({ href, name, subtitle, accent = false }:
        { href: string; name: string; subtitle?: string; accent?: boolean }) => (
        <Link
            href={href}
            className="group/li flex items-center gap-3 px-4 py-3 rounded-xl
                       hover:bg-[#D61C4E]/5 border border-transparent
                       hover:border-[#D61C4E]/10 transition-all duration-200"
        >
            <span className={`w-2 h-2 rounded-full flex-shrink-0 transition-colors
                ${accent ? 'bg-[#D61C4E]' : 'bg-slate-200 group-hover/li:bg-[#D61C4E]'}`} />
            <div className="flex flex-col min-w-0 flex-1">
                <span className="text-[12px] font-semibold text-slate-700
                                group-hover/li:text-[#D61C4E] transition-colors leading-snug">
                    {name}
                </span>
                {subtitle && (
                    <span className="text-[9px] text-slate-400 uppercase tracking-wide">{subtitle}</span>
                )}
            </div>
            <FontAwesomeIcon icon={faArrowRight}
                className="text-[8px] text-slate-300 group-hover/li:text-[#D61C4E]
                           group-hover/li:translate-x-0.5 transition-all flex-shrink-0" />
        </Link>
    );

    const dropdownAnim = "absolute top-[calc(100%+2px)] invisible group-hover:visible opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 transition-all duration-300 ease-out z-[1001]";

    return (
        <header className="relative w-full bg-white z-[1000] shadow-sm">

            {/* ── Promo ticker ── */}
            {headerPromoMerchant && headerPromoMerchant.length > 0 && (
                <div className="bg-[#0f172a] py-2">
                    <div className="container mx-auto px-4 flex justify-center items-center gap-8
                                    text-[9px] font-black uppercase tracking-[0.25em]">
                        {headerPromoMerchant.slice(0, 4).map((item, i) => (
                            <Link key={i} href={getMerchantHref(item, mer_slug, mer_slug_type)}
                                className="text-white/50 hover:text-white transition-colors
                                           flex items-center gap-2">
                                <span className="h-1 w-1 rounded-full bg-[#D61C4E] animate-pulse" />
                                {item?.promotional_tag || item?.merchant_name}
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Logo + Search ── */}
            <div className="container mx-auto px-4 py-3">
                <div className="flex items-center justify-between gap-10">
                    <div className="hidden lg:block shrink-0">
                        <Link href="/" className="inline-block hover:opacity-80 transition-opacity duration-300">
                            <Image src={companyLogo} height={68} width={140} alt="logo" className="object-contain" />
                        </Link>
                    </div>
                    <div className="flex-grow max-w-2xl hidden sm:block">
                        <Suspense fallback={<div className="h-11 bg-slate-50 rounded-xl animate-pulse" />}>
                            <NavSearch companyId={unique_id} mer_slug={mer_slug}
                                slug_type={mer_slug_type} cat_slug={cat_slug} domain={companyDomain} />
                        </Suspense>
                    </div>
                    <div className="flex lg:hidden items-center justify-between w-full sm:w-auto">
                        <Link href="/" className="w-36">
                            <Image src={companyLogo} height={68} width={140} alt="logo" className="object-contain" />
                        </Link>
                        <MobileNavMenu {...{
                            categories, merchantData, events, promotions,
                            cat_slug, mer_slug, mer_slug_type, promo_slug,
                            blog_url: currentBlogUrl, blog_title, companyLogo,
                            companyId: unique_id, headerPromoMerchant, companyDomain,
                        }} />
                    </div>
                </div>
            </div>

            {/* ── Main nav ── */}
            <nav className="hidden lg:block border-t border-slate-100">
                <div className="container mx-auto px-4">
                    <ul className="flex items-stretch justify-center
                                   text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">

                        {/* Home */}
                        <li className="relative group">
                            <Link href="/" className="px-6 py-5 flex items-center gap-2
                                                      hover:text-[#D61C4E] transition-colors relative">
                                Home
                                <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[#D61C4E]
                                                scale-x-0 group-hover:scale-x-100
                                                transition-transform duration-300 origin-center" />
                            </Link>
                        </li>

                        {/* ════ CATEGORIES ════ */}
                        <li className="relative group">
                            <Link href={`/${cat_slug}`}
                                className="px-6 py-5 flex items-center gap-2
                                           group-hover:text-[#D61C4E] transition-colors relative">
                                Categories
                                <FontAwesomeIcon icon={faChevronDown}
                                    className="text-[8px] group-hover:rotate-180 transition-transform duration-300" />
                                <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[#D61C4E]
                                                scale-x-0 group-hover:scale-x-100
                                                transition-transform duration-300 origin-center" />
                            </Link>

                            <div className={`${dropdownAnim} left-0 w-[600px]`}>
                                {/* Arrow pointer */}
                                <div className="w-3 h-3 bg-white border-l border-t border-slate-100
                                               rotate-45 ml-16 -mb-1.5 relative z-10 shadow-[-1px_-1px_0_#f1f5f9]" />
                                <div className="bg-white rounded-2xl border border-slate-100
                                               shadow-[0_20px_60px_-10px_rgba(0,0,0,0.15)] overflow-hidden">
                                    {/* Header */}
                                    <div className="flex items-center justify-between px-5 py-3.5
                                                   bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded-lg bg-[#D61C4E] flex items-center justify-center">
                                                <FontAwesomeIcon icon={faBolt} className="text-white text-[8px]" />
                                            </div>
                                            <span className="text-[10px] font-black text-slate-700 uppercase tracking-[0.2em]">
                                                Shop by Category
                                            </span>
                                        </div>
                                        <Link href={`/${cat_slug}`}
                                            className="flex items-center gap-1.5 text-[10px] font-black
                                                       text-[#D61C4E] hover:gap-2.5 transition-all">
                                            View All
                                            <FontAwesomeIcon icon={faArrowRight} className="text-[8px]" />
                                        </Link>
                                    </div>
                                    {/* Icon grid */}
                                    <div className="grid grid-cols-5 gap-1 p-4 max-h-[340px] overflow-y-auto">
                                        {categories?.map((item: any, i: number) => (
                                            <CategoryCard
                                                key={i}
                                                href={`/${item?.url}`}
                                                name={item.name}
                                                iconSrc={getBaseImageUrl(companyDomain, item?.category_image, "")}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </li>

                        {/* ════ STORES ════ */}
                        <li className="relative group">
                            <Link href="/all-stores/A"
                                className="px-6 py-5 flex items-center gap-2
                                           group-hover:text-[#D61C4E] transition-colors relative">
                                Stores
                                <FontAwesomeIcon icon={faChevronDown}
                                    className="text-[8px] group-hover:rotate-180 transition-transform duration-300" />
                                <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[#D61C4E]
                                                scale-x-0 group-hover:scale-x-100
                                                transition-transform duration-300 origin-center" />
                            </Link>

                            <div className={`${dropdownAnim} left-0 w-[600px]`}>
                                <div className="w-3 h-3 bg-white border-l border-t border-slate-100
                                               rotate-45 ml-36 -mb-1.5 relative z-10" />
                                <div className="bg-white rounded-2xl border border-slate-100
                                               shadow-[0_20px_60px_-10px_rgba(0,0,0,0.15)] overflow-hidden">
                                    {/* Same header as Categories */}
                                    <div className="flex items-center justify-between px-5 py-3.5
                                                   bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded-lg bg-[#D61C4E] flex items-center justify-center">
                                                <FontAwesomeIcon icon={faFire} className="text-white text-[8px]" />
                                            </div>
                                            <span className="text-[10px] font-black text-slate-700 uppercase tracking-[0.2em]">
                                                Top Stores
                                            </span>
                                        </div>
                                        <Link href="/all-stores/A"
                                            className="flex items-center gap-1.5 text-[10px] font-black
                                                       text-[#D61C4E] hover:gap-2.5 transition-all">
                                            View All
                                            <FontAwesomeIcon icon={faArrowRight} className="text-[8px]" />
                                        </Link>
                                    </div>
                                    {/* Same 5-col icon grid as Categories */}
                                    <div className="grid grid-cols-5 gap-1 p-4 max-h-[340px] overflow-y-auto">
                                        {merchantData?.map((item, i) => (
                                            <CategoryCard
                                                key={i}
                                                href={getMerchantHref(item, mer_slug, mer_slug_type)}
                                                name={item.merchant_name}
                                                iconSrc={getBaseImageUrl(companyDomain, item?.merchant_logo, "")}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </li>

                        {/* ════ PRODUCTS ════ */}
                        <li className="relative group">
                            <Link href="/all-products"
                                className="px-6 py-5 flex items-center gap-2
                                           group-hover:text-[#D61C4E] transition-colors relative">
                                Products
                                <FontAwesomeIcon icon={faChevronDown}
                                    className="text-[8px] group-hover:rotate-180 transition-transform duration-300" />
                                <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[#D61C4E]
                                                scale-x-0 group-hover:scale-x-100
                                                transition-transform duration-300 origin-center" />
                            </Link>

                            <div className={`${dropdownAnim} left-0 w-[240px]`}>
                                <div className="w-3 h-3 bg-white border-l border-t border-slate-100
                                               rotate-45 ml-24 -mb-1.5 relative z-10" />
                                <div className="bg-white rounded-2xl border border-slate-100
                                               shadow-[0_20px_60px_-10px_rgba(0,0,0,0.15)] p-2">
                                    <ListItem href="/products" name="Brand Products" subtitle="Shop by brand" accent />
                                    <ListItem href="/all-products" name="All Products" subtitle="Browse everything" />
                                </div>
                            </div>
                        </li>

                        {/* ════ PROMOTIONS ════ */}
                        {promotions?.length > 0 && (
                            <li className="relative group">
                                <Link href={`/${promo_slug}`}
                                    className="px-6 py-5 flex items-center gap-2
                                               group-hover:text-[#D61C4E] transition-colors relative">
                                    Promotions
                                    <FontAwesomeIcon icon={faChevronDown}
                                        className="text-[8px] group-hover:rotate-180 transition-transform duration-300" />
                                    <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[#D61C4E]
                                                    scale-x-0 group-hover:scale-x-100
                                                    transition-transform duration-300 origin-center" />
                                </Link>

                                <div className={`${dropdownAnim} left-0 w-[280px]`}>
                                    <div className="w-3 h-3 bg-white border-l border-t border-slate-100
                                                   rotate-45 ml-24 -mb-1.5 relative z-10" />
                                    <div className="bg-white rounded-2xl border border-slate-100
                                                   shadow-[0_20px_60px_-10px_rgba(0,0,0,0.15)] overflow-hidden">
                                        {/* Colourful header */}
                                        <div className="px-5 py-4 bg-gradient-to-r from-[#D61C4E] to-[#ff4d7d]">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <FontAwesomeIcon icon={faTags} className="text-white text-xs" />
                                                <span className="text-[10px] font-black text-white uppercase tracking-[0.2em]">
                                                    Active Promotions
                                                </span>
                                            </div>
                                            <p className="text-[9px] text-white/70 font-medium">
                                                Limited-time offers
                                            </p>
                                        </div>
                                        {/* ~5 items visible (~56px each) then scroll */}
                                        <div className="p-2 max-h-[280px] overflow-y-auto overscroll-contain">
                                            {promotions.map((item, i) => (
                                                <ListItem key={i}
                                                    href={getPromotionHref(item, promo_slug)}
                                                    name={item.name}
                                                    subtitle="Limited Offer"
                                                    accent={i === 0}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        )}

                        {/* ════ EVENTS ════ */}
                        {events?.length > 0 && (
                            <li className="relative group">
                                <Link href="/events"
                                    className="px-6 py-5 flex items-center gap-2
                                               group-hover:text-[#D61C4E] transition-colors relative">
                                    Events
                                    <FontAwesomeIcon icon={faChevronDown}
                                        className="text-[8px] group-hover:rotate-180 transition-transform duration-300" />
                                    <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[#D61C4E]
                                                    scale-x-0 group-hover:scale-x-100
                                                    transition-transform duration-300 origin-center" />
                                </Link>

                                <div className={`${dropdownAnim} left-0 w-[280px]`}>
                                    <div className="w-3 h-3 bg-white border-l border-t border-slate-100
                                                   rotate-45 ml-36 -mb-1.5 relative z-10" />
                                    <div className="bg-white rounded-2xl border border-slate-100
                                                   shadow-[0_20px_60px_-10px_rgba(0,0,0,0.15)] overflow-hidden">
                                        {/* Same colourful header as Promotions */}
                                        <div className="px-5 py-4 bg-gradient-to-r from-[#D61C4E] to-[#ff4d7d]">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <FontAwesomeIcon icon={faCalendarCheck} className="text-white text-xs" />
                                                <span className="text-[10px] font-black text-white uppercase tracking-[0.2em]">
                                                    Upcoming Events
                                                </span>
                                            </div>
                                            <p className="text-[9px] text-white/70 font-medium">
                                                Global shopping festivals
                                            </p>
                                        </div>
                                        {/* ~5 items visible then scroll */}
                                        <div className="p-2 max-h-[280px] overflow-y-auto overscroll-contain">
                                            {events.map((item, i) => (
                                                <ListItem
                                                    key={i}
                                                    href={getEventsHref(item, mer_slug_type)}
                                                    name={item.name}
                                                    subtitle="Event Special"
                                                    accent={i === 0}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        )}

                        {/* ════ BLOG ════ */}
                        {blog_title && currentBlogUrl && (
                            <li className="relative group">
                                <Link href={currentBlogUrl}
                                    className="px-6 py-5 flex items-center gap-2
                                               hover:text-[#D61C4E] transition-colors relative">
                                    {blog_title}
                                    <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[#D61C4E]
                                                    scale-x-0 group-hover:scale-x-100
                                                    transition-transform duration-300 origin-center" />
                                </Link>
                            </li>
                        )}

                    </ul>
                </div>
            </nav>
        </header>
    );
};

export default NavbarSec;
